import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { EmptyState, Pill, HostChip, showToast } from "../primitives";
import type { Persona } from "../primitives";
import { Panel, PanelRow } from "./Panel";
import { BulkActionBar, BulkCheckbox } from "./BulkActionBar";
import { Markdown } from "../Markdown";
import { useChildNodes } from "../../hooks/use-child-nodes";
import { useBulkSelect } from "../../hooks/use-bulk-select";
import { useIsOwner } from "../../hooks/use-is-owner";
import {
  useArchiveNodeAction,
  useRestoreNodeAction,
  useDeleteNodeAction,
} from "../../hooks/use-node-actions";
import { PanelRowMenu } from "./PanelRowMenu";
import { ConfirmDeleteRowModal } from "./ConfirmDeleteRowModal";
import { ArchivedBadge, ShowArchivedChip, useShowArchived } from "./panel-archive";
import { MoveToModal } from "../scope/MoveToModal";
import { ArchiveModal } from "../scope/ArchiveModal";
import { SupersedeMemoryModal } from "../memory/SupersedeMemoryModal";
import { MemoryHistoryDrawer } from "../memory/MemoryHistoryDrawer";
import { useSemanticMemorySearch, type SemanticMemoryResult } from "../../hooks/use-semantic-memory-search";
import { MemoryProvenanceChip } from "../memory/MemoryProvenanceChip";
import { PatternMemoryCard } from "./PatternMemoryCard";
import { isPatternMemory } from "../../hooks/use-agent-learnings";
import type { LibraryNode, MemoryType } from "../../../lib/types";

interface MemoriesPanelProps {
  nodeId: string;
  /**
   * Sprint 23.6 — when set, the panel mounts with that memory-type chip active
   * (the agent-profile Memories tab passes "feedback" so Learnings is the
   * default view per Q10). Omitted elsewhere → "All".
   */
  defaultFilter?: MemoryType;
}

const MEMORY_FILTERS: { value: MemoryType | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "feedback", label: "Feedback" },
  { value: "user", label: "User" },
  { value: "project", label: "Project" },
  { value: "reference", label: "Reference" },
];

const MEMORY_TYPE_META: Record<MemoryType, { variant: Persona; label: string }> = {
  user: { variant: "blue", label: "user" },
  feedback: { variant: "amber", label: "feedback" },
  project: { variant: "green", label: "project" },
  reference: { variant: "gray", label: "reference" },
};

function memoryTypeMeta(type: MemoryType | null | undefined): { variant: Persona; label: string } {
  if (type && type in MEMORY_TYPE_META) return MEMORY_TYPE_META[type];
  return { variant: "gray", label: "memory" };
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function MemoriesPanel({ nodeId, defaultFilter }: MemoriesPanelProps) {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useShowArchived("memories", nodeId);
  const memories = useChildNodes(nodeId, "memory", showArchived);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  // Sprint 23.6 — memory-type filter chip. Defaults to the caller's
  // `defaultFilter` (feedback on the profile Memories tab), else "All".
  const [typeFilter, setTypeFilter] = useState<MemoryType | "all">(defaultFilter ?? "all");
  // Sprint 20: semantic search. `query` is the live input; `submitted` is the
  // active search (set on Enter / icon click) that flips the panel into results
  // mode. Empty submitted = normal browse.
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");

  const allItems = memories.data ?? [];
  const items =
    typeFilter === "all"
      ? allItems
      : allItems.filter((m) => (m.memory_type ?? null) === typeFilter);
  const bulk = useBulkSelect(items);
  const searching = submitted.trim().length > 0;

  return (
    <>
      {bulk.active && (
        <BulkActionBar
          count={bulk.count}
          onMove={() => setMoveOpen(true)}
          onArchive={() => setArchiveOpen(true)}
          onCancel={bulk.clear}
        />
      )}
      <SemanticSearchBar
        value={query}
        onChange={setQuery}
        onSubmit={() => setSubmitted(query)}
        onClear={() => { setQuery(""); setSubmitted(""); }}
        active={searching}
      />
      {searching ? (
        <SemanticResultsPanel nodeId={nodeId} query={submitted} onOpen={(id) => navigate(`/library/${id}`)} />
      ) : (
        <>
        <div style={memFilterRow}>
          {MEMORY_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setTypeFilter(f.value)}
              style={typeFilter === f.value ? memChipActive : memChipIdle}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Panel
          title="Memories"
          count={items.length}
          action={<ShowArchivedChip value={showArchived} onChange={setShowArchived} />}
          isLoading={memories.isLoading}
          error={memories.error}
          onRetry={() => memories.refetch()}
          isEmpty={items.length === 0}
          empty={
            showArchived ? (
              <EmptyState>No archived items. Items you archive will appear here for restore.</EmptyState>
            ) : (
              <EmptyState>No memories yet. Ask Claude to remember something.</EmptyState>
            )
          }
        >
          {items.map((memory) => (
            <MemoryRow
              key={memory.id}
              memory={memory}
              expanded={expandedId === memory.id}
              bulkActive={bulk.active}
              selected={bulk.isSelected(memory.id)}
              onSelectToggle={(e) => bulk.toggle(memory.id, e)}
              onToggle={() =>
                bulk.active
                  ? bulk.toggle(memory.id)
                  : setExpandedId((cur) => (cur === memory.id ? null : memory.id))
              }
              onOpen={() => navigate(`/library/${memory.id}`)}
            />
          ))}
        </Panel>
        </>
      )}

      <MoveToModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        sources={bulk.selectedItems}
        onMoved={() => bulk.clear()}
      />
      <ArchiveModal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        nodes={bulk.selectedItems}
        onArchived={() => bulk.clear()}
      />
    </>
  );
}

const memFilterRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 8,
};

const memChipBase: CSSProperties = {
  fontSize: 11,
  padding: "3px 9px",
  borderRadius: 999,
  border: "0.5px solid transparent",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const memChipIdle: CSSProperties = memChipBase;

const memChipActive: CSSProperties = {
  ...memChipBase,
  background: "var(--color-background-secondary)",
  borderColor: "var(--color-border-tertiary)",
  color: "var(--color-text-primary)",
  fontWeight: 500,
};

// ---------------------------------------------------------------------------
// Sprint 20 — semantic search bar + results
// ---------------------------------------------------------------------------

function SemanticSearchBar({
  value,
  onChange,
  onSubmit,
  onClear,
  active,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onClear: () => void;
  active: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 8px 8px" }}>
      <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
        <i className="ti ti-search" style={{ position: "absolute", left: 9, fontSize: 14, color: "var(--color-text-tertiary)", pointerEvents: "none" }} aria-hidden="true" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
            if (e.key === "Escape") onClear();
          }}
          placeholder="Search memories by meaning…"
          aria-label="Semantic memory search"
          style={{
            width: "100%",
            fontSize: 13,
            padding: "7px 28px 7px 28px",
            border: "var(--kuvio-border-default)",
            borderRadius: "var(--border-radius-md)",
            background: "var(--color-background-primary)",
            color: "var(--color-text-primary)",
            fontFamily: "var(--font-sans)",
            boxSizing: "border-box",
          }}
        />
        {(value || active) && (
          <button onClick={onClear} aria-label="Clear search" style={{ position: "absolute", right: 6, border: "none", background: "transparent", cursor: "pointer", color: "var(--color-text-tertiary)", padding: 2, display: "flex" }}>
            <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

function SemanticResultsPanel({
  nodeId,
  query,
  onOpen,
}: {
  nodeId: string;
  query: string;
  onOpen: (id: string) => void;
}) {
  const search = useSemanticMemorySearch({ query, agent_id: nodeId });
  const results = search.data?.results ?? [];
  const mode = search.data?.mode ?? "semantic";

  return (
    <Panel
      title="Search results"
      count={results.length}
      action={
        mode === "keyword" ? (
          <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>keyword fallback</span>
        ) : undefined
      }
      isLoading={search.isLoading}
      error={search.error}
      onRetry={() => search.refetch()}
      isEmpty={results.length === 0}
      empty={<EmptyState>No memories match “{query}”.</EmptyState>}
    >
      {results.map((r) => (
        <SemanticResultRow key={r.id} result={r} onOpen={() => onOpen(r.id)} />
      ))}
    </Panel>
  );
}

function SemanticResultRow({ result, onOpen }: { result: SemanticMemoryResult; onOpen: () => void }) {
  const meta = memoryTypeMeta(result.memory_type as MemoryType | null);
  const superseded = result.status === "superseded";
  // S20.33: only surface a score chip for a meaningful match.
  const showScore = typeof result.score === "number" && result.score >= 0.4;
  return (
    <PanelRow onClick={onOpen} title={`Open ${result.name}`}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
        <Pill variant={meta.variant}>{meta.label}</Pill>
        <span style={{ ...nameStyle, textDecoration: superseded ? "line-through" : "none" }}>{result.name}</span>
        {superseded && <Pill variant="amber">superseded</Pill>}
        <span style={{ fontSize: 11, color: "var(--color-text-tertiary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {result.preview}
        </span>
        {showScore && (
          <span style={scoreChipStyle} title="Similarity">{Math.round((result.score as number) * 100)}%</span>
        )}
      </span>
    </PanelRow>
  );
}

function MemoryRow({
  memory,
  expanded,
  bulkActive,
  selected,
  onSelectToggle,
  onToggle,
  onOpen,
}: {
  memory: LibraryNode;
  expanded: boolean;
  bulkActive: boolean;
  selected: boolean;
  onSelectToggle: (e: { shiftKey?: boolean }) => void;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const meta = memoryTypeMeta(memory.memory_type);
  const body = memory.document ?? "";

  const archiveNode = useArchiveNodeAction();
  const restoreNode = useRestoreNodeAction();
  const deleteNode = useDeleteNodeAction();
  const canDelete = useIsOwner();
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Sprint 20 — temporal validity affordances.
  const [supersedeOpen, setSupersedeOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const isSuperseded = memory.status === "superseded";

  const isArchived = !!memory.archived_at;

  function handleArchive() {
    archiveNode.mutate(
      { id: memory.id },
      {
        onSuccess: () =>
          showToast(`Archived '${memory.name}'`, "success", false, {
            label: "Undo",
            onClick: () => restoreNode.mutate({ id: memory.id }),
          }, 10000),
        onError: (e) => showToast(e instanceof Error ? e.message : "Archive failed", "error", true),
      },
    );
  }

  function handleRestore() {
    restoreNode.mutate(
      { id: memory.id },
      {
        onSuccess: () => showToast(`Restored '${memory.name}'`),
        onError: (e) => showToast(e instanceof Error ? e.message : "Restore failed", "error", true),
      },
    );
  }

  const bodyWrap: CSSProperties = {
    margin: "2px 8px 8px",
    padding: "10px 12px",
    background: "var(--color-background-secondary)",
    borderRadius: "var(--border-radius-md)",
    maxHeight: 600,
    overflowY: "auto",
  };

  return (
    <div>
      <PanelRow
        onClick={onToggle}
        title={bulkActive ? (selected ? "Deselect" : "Select") : expanded ? "Collapse" : "Show memory"}
        trailing={
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
              aria-label={`Open ${memory.name}`}
              style={openIconStyle}
            >
              <i className="ti ti-external-link" style={{ fontSize: 14 }} aria-hidden="true" />
            </button>
            <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
              <PanelRowMenu
                isArchived={isArchived}
                canDelete={canDelete}
                extraItems={[
                  { label: "History", icon: "history", onClick: () => setHistoryOpen(true) },
                  ...(canDelete && !isSuperseded
                    ? [{ label: "Supersede…", icon: "exchange", onClick: () => setSupersedeOpen(true) }]
                    : []),
                ]}
                onArchive={handleArchive}
                onRestore={handleRestore}
                onDelete={() => setConfirmDelete(true)}
              />
            </span>
          </div>
        }
      >
        <BulkCheckbox
          checked={selected}
          visible={bulkActive}
          onToggle={onSelectToggle}
          label={`Select ${memory.name}`}
        />
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1, opacity: isArchived ? 0.6 : 1 }}>
          <Pill variant={meta.variant}>{meta.label}</Pill>
          <span style={{ ...nameStyle, textDecoration: isSuperseded ? "line-through" : "none" }}>{memory.name}</span>
          {isSuperseded && (
            <span
              onClick={(e) => { e.stopPropagation(); setHistoryOpen(true); }}
              style={{ display: "inline-flex", cursor: "pointer" }}
              title="View supersession history"
            >
              <Pill variant="amber">superseded</Pill>
            </span>
          )}
          {isArchived && <ArchivedBadge />}
          {/* Sprint 21 — memory provenance chip (user / agent / retrospective / pattern). */}
          <MemoryProvenanceChip node={memory} />
          {memory.created_via && <HostChip kind={memory.created_via} />}
          <span style={timeStyle}>{formatRelativeTime(memory.updated_at)}</span>
        </span>
      </PanelRow>
      <ConfirmDeleteRowModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        entityKind="memory"
        entityName={memory.name}
        entityId={memory.id}
        backRefKind="node"
        onConfirm={async ({ force }) => {
          await deleteNode.mutateAsync({ id: memory.id, force });
          showToast(`Deleted '${memory.name}'`);
        }}
      />
      <SupersedeMemoryModal
        open={supersedeOpen}
        onClose={() => setSupersedeOpen(false)}
        memory={memory}
      />
      <MemoryHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        memory={memory}
      />
      {expanded && (
        <div style={bodyWrap}>
          {/* Sprint 21 — pattern memories get the interaction card (boost / reduce / not-a-pattern). */}
          {isPatternMemory(memory) && (
            <div style={{ marginBottom: 10 }}>
              <PatternMemoryCard memory={memory} />
            </div>
          )}
          {body.trim() ? (
            <Markdown>{body}</Markdown>
          ) : (
            <div style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>This memory is empty.</div>
          )}
          <div style={{ marginTop: 10 }}>
            <button onClick={onOpen} style={openFullStyle}>
              Open full memory
              <i className="ti ti-arrow-right" style={{ fontSize: 12 }} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const nameStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const timeStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
  marginLeft: "auto",
};

// Sprint 20 — semantic match score chip on a search-result row.
const scoreChipStyle: CSSProperties = {
  marginLeft: "auto",
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 600,
  color: "var(--color-text-info)",
  background: "var(--color-background-info-subtle, var(--color-background-secondary))",
  borderRadius: "var(--border-radius-sm)",
  padding: "1px 6px",
  whiteSpace: "nowrap",
};

const openIconStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  padding: 2,
  display: "flex",
  alignItems: "center",
};

const openFullStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontFamily: "var(--font-sans)",
};
